import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { superAdminAPI } from '../../services/api';
import {
  DateRangeFilter, FilterSelect, ReportFilterBar, EMPTY_REPORT_FILTERS, filterReportRows, uniqueSorted,
} from '../../components/SuperAdmin/ListTools';
import { inDateRange, formatExportDate } from '../../utils/exportData';
import {
  HiOutlineOfficeBuilding,
  HiOutlineRefresh,
  HiOutlineCalendar,
  HiOutlineDocumentText,
  HiOutlineX,
  HiOutlineExclamation,
  HiOutlineClock,
} from 'react-icons/hi';
import { ReportGuide } from '../../components/SuperAdmin/ReportGuide';

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const REASON_LABELS = {
  pricing_too_high: 'Pricing too high',
  budget_constraints: 'Budget constraints',
  switched_competitor: 'Switched competitor',
  product_gap: 'Product gap',
  business_closed: 'Business closed',
  no_response: 'No response',
  other: 'Other',
};

const REASON_COLORS = {
  pricing_too_high: 'bg-orange-100 text-orange-700',
  budget_constraints: 'bg-amber-100 text-amber-700',
  switched_competitor: 'bg-purple-100 text-purple-700',
  product_gap: 'bg-blue-100 text-blue-700',
  business_closed: 'bg-gray-100 text-gray-600',
  no_response: 'bg-yellow-100 text-yellow-700',
  other: 'bg-gray-100 text-gray-600',
};

const STATUS_BADGES = {
  active: 'bg-green-100 text-green-700',
  trial: 'bg-blue-100 text-blue-700',
  suspended: 'bg-red-100 text-red-700',
  cancelled: 'bg-red-100 text-red-700',
};

const SubscriptionExpiryReport = () => {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState('all');
  const [recordTarget, setRecordTarget] = useState(null);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['subscription-expiry-report', filter],
    queryFn: () => superAdminAPI.getSubscriptionExpiryReport({ filter }),
    refetchInterval: 120000,
  });

  const recordMutation = useMutation({
    mutationFn: superAdminAPI.recordRenewalLossReason,
    onSuccess: () => {
      toast.success('Lost reason recorded');
      setRecordTarget(null);
      setNotes('');
      queryClient.invalidateQueries({ queryKey: ['subscription-expiry-report'] });
      queryClient.invalidateQueries({ queryKey: ['renewal-loss-report'] });
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to record reason'),
  });

  // Axios → res.data (server body) → body.data
  const body = data?.data?.data || {};
  const stats = body.stats || {};
  const allRows = Array.isArray(body.rows) ? body.rows : [];
  const [rowFilters, setRowFilters] = useState({
    ...EMPTY_REPORT_FILTERS, plan: '', status: '', kind: '', reason: '', from: '', to: '',
  });
  const rows = filterReportRows(allRows, rowFilters, {
    searchOf: (r) => [r.organizationName, r.organizationEmail, r.organizationPhone, r.planName, r.lossNotes],
  }).filter((r) =>
    (!rowFilters.plan || r.planName === rowFilters.plan)
    && (!rowFilters.status || r.orgStatus === rowFilters.status)
    && (!rowFilters.kind || (rowFilters.kind === 'trial' ? r.isTrial : !r.isTrial))
    && (!rowFilters.reason || (rowFilters.reason === 'not_recorded' ? !r.lossReason : r.lossReason === rowFilters.reason))
    && inDateRange(r.subscriptionEndsAt, rowFilters.from, rowFilters.to));

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex-1 basis-80">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <HiOutlineCalendar className="w-7 h-7 text-primary-500" />
            Subscription End Dates
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Every organization sorted by when its subscription ends
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium
                     hover:bg-primary-600 shadow-lg shadow-primary-500/25 disabled:opacity-50"
        >
          <HiOutlineRefresh className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <ReportGuide
        id="subscription-expiry"
        summary="A simple list of when each organization’s subscription ends."
        points={[
          ["Expiring in 30 days", "Ends within the next month: follow up now."],
          ["Expired", "End date has already passed."],
          ["Lost reason", "Why an expired customer did not renew."],
        ]}
        tip="Click a box at the top to filter the list."
      />

      {/* Stat tiles + filter tabs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { key: 'all', label: 'Total Organizations', value: stats.total ?? 0, icon: HiOutlineOfficeBuilding, color: 'text-primary-500' },
          { key: 'expiring', label: 'Expiring in 30 days', value: stats.expiringSoon ?? 0, icon: HiOutlineClock, color: 'text-amber-500' },
          { key: 'expired', label: 'Expired', value: stats.expired ?? 0, icon: HiOutlineExclamation, color: 'text-red-500' },
        ].map((t, idx) => {
          const Icon = t.icon;
          return (
            <button
              key={idx}
              onClick={() => setFilter(t.key)}
              className={`bg-white rounded-xl border p-4 flex items-center gap-3 text-left transition-all ${
                filter === t.key
                  ? 'border-primary-300 ring-2 ring-primary-500/20'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center">
                <Icon className={`w-5 h-5 ${t.color}`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{t.value}</p>
                <p className="text-xs text-gray-500">{t.label}</p>
              </div>
            </button>
          );
        })}
        {/* Stat-only tile (not a filter) */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center">
            <HiOutlineDocumentText className="w-5 h-5 text-purple-500" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{stats.reasonsRecorded ?? 0}</p>
            <p className="text-xs text-gray-500">Loss Reasons Recorded</p>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-700 capitalize">
            {filter === 'all' ? 'All Organizations' : filter === 'expiring' ? 'Expiring Soon' : 'Expired'} — by Subscription End Date
          </h2>
          <span className="text-xs text-gray-400">
            {rows.length === allRows.length ? `${rows.length} shown` : `${rows.length} of ${allRows.length} shown`}
          </span>
        </div>

        {allRows.length > 0 && (
          <ReportFilterBar
            filters={rowFilters}
            onChange={setRowFilters}
            showPriority={false}
            searchPlaceholder="Search organization, email, phone, plan…"
            extraActive={!!(rowFilters.plan || rowFilters.status || rowFilters.kind || rowFilters.reason || rowFilters.from || rowFilters.to)}
            onResetExtra={() => setRowFilters((f) => ({ ...f, plan: '', status: '', kind: '', reason: '', from: '', to: '' }))}
            exportProps={{
              fileName: `subscription-expiry-${filter}`,
              getRows: () => rows,
              columns: [
                { header: 'Organization', value: (r) => r.organizationName },
                { header: 'Email', value: (r) => r.organizationEmail },
                { header: 'Phone', value: (r) => r.organizationPhone },
                { header: 'Plan', value: (r) => r.planName },
                { header: 'Trial', value: (r) => !!r.isTrial },
                { header: 'Subscription Ends', value: (r) => formatExportDate(r.subscriptionEndsAt, false) },
                { header: 'Days Left', value: (r) => r.daysLeft },
                { header: 'Org Status', value: (r) => r.orgStatus },
                { header: 'Category', value: (r) => r.category },
                { header: 'Lost Reason', value: (r) => (r.lossReason ? REASON_LABELS[r.lossReason] || r.lossReason : '') },
                { header: 'Notes', value: (r) => r.lossNotes },
              ],
            }}
          >
            <FilterSelect value={rowFilters.plan} onChange={(v) => setRowFilters((f) => ({ ...f, plan: v }))}
              allLabel="All Plans" options={uniqueSorted(allRows.map((r) => r.planName)).map((p) => [p, p])} />
            <FilterSelect value={rowFilters.status} onChange={(v) => setRowFilters((f) => ({ ...f, status: v }))}
              allLabel="All Org Status" options={uniqueSorted(allRows.map((r) => r.orgStatus)).map((s) => [s, s])} />
            <FilterSelect value={rowFilters.kind} onChange={(v) => setRowFilters((f) => ({ ...f, kind: v }))}
              allLabel="Trial & Paid" options={[['trial', 'Trial only'], ['paid', 'Paid only']]} />
            <FilterSelect value={rowFilters.reason} onChange={(v) => setRowFilters((f) => ({ ...f, reason: v }))}
              allLabel="All Lost Reasons" options={[['not_recorded', 'Not recorded'], ...Object.entries(REASON_LABELS)]} />
            <DateRangeFilter label="Ends" from={rowFilters.from} to={rowFilters.to}
              onChange={({ from, to }) => setRowFilters((f) => ({ ...f, from, to }))} />
          </ReportFilterBar>
        )}

        {isLoading ? (
          <div className="p-8 text-center text-gray-400 text-sm">Loading report…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-400">
            {allRows.length > 0 ? 'No organizations match these filters' : 'No organizations in this view'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[950px]">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">S.No</th>
                  <th className="px-4 py-3">Organization</th>
                  <th className="px-4 py-3">Phone Number</th>
                  <th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3">Subscription Ends</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Lost Reason</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row, i) => (
                  <tr key={row.organizationId} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-400">{i + 1}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-2">
                        <HiOutlineOfficeBuilding className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900">{row.organizationName}</p>
                          <p className="text-xs text-gray-400 truncate max-w-[200px]">{row.organizationEmail || '—'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {row.organizationPhone || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{row.planName || '—'}</td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-gray-600">{formatDate(row.subscriptionEndsAt)}</p>
                        {row.isTrial && <p className="text-xs text-blue-500 font-medium">Trial end</p>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col items-start gap-1">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${
                          STATUS_BADGES[row.orgStatus] || 'bg-gray-100 text-gray-600'
                        }`}>
                          {row.orgStatus}
                        </span>
                        {row.daysLeft < 0 ? (
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                            {Math.abs(row.daysLeft)}d overdue
                          </span>
                        ) : row.category === 'expiring' ? (
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                            in {row.daysLeft}d
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {row.lossReason ? (
                        <div>
                          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${REASON_COLORS[row.lossReason] || 'bg-gray-100 text-gray-600'}`}>
                            {REASON_LABELS[row.lossReason] || row.lossReason}
                          </span>
                          {row.lossNotes && (
                            <p className="text-xs text-gray-400 mt-1 max-w-[220px] truncate" title={row.lossNotes}>
                              {row.lossNotes}
                            </p>
                          )}
                        </div>
                      ) : row.category === 'expired' ? (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                          <HiOutlineExclamation className="w-3.5 h-3.5 text-amber-400" />
                          Not recorded
                        </span>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end">
                        {row.category === 'expired' && (
                          <button
                            onClick={() => {
                              setRecordTarget(row);
                              setReason(row.lossReason || '');
                              setNotes(row.lossNotes || '');
                            }}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-primary-200
                                       text-primary-600 text-xs font-medium hover:bg-primary-50 whitespace-nowrap"
                          >
                            <HiOutlineDocumentText className="w-3.5 h-3.5" />
                            {row.lossReason ? 'Update Reason' : 'Record Reason'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Record-lost-reason modal (shares the renewal-loss endpoint) */}
      {recordTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Record Lost Reason</h3>
              <button onClick={() => setRecordTarget(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <HiOutlineX className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <p className="text-sm text-gray-600">
                <span className="font-semibold text-gray-900">{recordTarget.organizationName}</span>
                {' '}— subscription expired {formatDate(recordTarget.subscriptionEndsAt)}
                {' ('}{recordTarget.daysLeft < 0 ? `${Math.abs(recordTarget.daysLeft)} day${Math.abs(recordTarget.daysLeft) !== 1 ? 's' : ''} overdue` : 'expired'}{')'}
              </p>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  Reason
                </label>
                <select
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                >
                  <option value="" disabled>Select a reason…</option>
                  {Object.entries(REASON_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  Notes (optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Any additional context…"
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => setRecordTarget(null)}
                className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  recordMutation.mutate({
                    organizationId: recordTarget.organizationId,
                    dueDate: recordTarget.subscriptionEndsAt,
                    reason,
                    notes,
                  })
                }
                disabled={!reason || recordMutation.isLoading}
                className="px-4 py-2 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 disabled:opacity-50"
              >
                {recordMutation.isLoading ? 'Saving…' : 'Save Reason'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionExpiryReport;